import { FooterModalButtons } from "@/controls/buttons/FooterModalButtons";
import { MILL_1, type MillId } from "@/setup/mill-config/mill-config";
import { ErrorModal, HelmetDexteel, MesfModal } from "@dexteel/mesf-core";
import { Grid2, MenuItem, TextField } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ChangeEvent, useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm, useWatch } from "react-hook-form";
import {
	EDGER_ORIENTATION_OPTIONS,
	STAND_TYPE_OPTIONS,
	SectionStand,
} from "../../../../models/Section";
import { upsertSectionStand } from "../../../../repositories/SectionRepository";

type Props = {
	show: boolean;
	onHide: () => void;
	sectionStand: SectionStand;
	assetId: MillId;
};

export const SectionStandModal = ({
	show,
	onHide,
	sectionStand,
	assetId,
}: Props) => {
	const isM1 = assetId === MILL_1.id;

	const [error, setError] = useState("");
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);

	const { control, reset, handleSubmit, setValue } = useForm<SectionStand>({
		mode: "onChange",
	});
	const standTypeCode = useWatch({ control, name: "StandTypeCode" });

	const queryClient = useQueryClient();
	const upsertMutation = useMutation(upsertSectionStand, {
		meta: {
			successMessage: `The stand ${sectionStand?.StandName} was configured successfully`,
		},
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ["sectionStands"] });
			onHide();
		},
		onError: (error: Error) => {
			setError(error.message);
		},
		onSettled: () => setIsSubmitLoading(false),
	});

	const onSubmit: SubmitHandler<SectionStand> = async (data) => {
		setIsSubmitLoading(true);
		upsertMutation.mutate({ sectionStand: data, defaultAreaId: assetId });
	};

	const onChangeRealType = (
		e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
		onChange: (value: string) => void,
	) => {
		const value = e.target.value.replace(",", ".");
		if (value === "" || /^(\d*\.?\d{0,3}|\d+\.)$/.test(value)) {
			onChange(value);
		}
	};
	const onBlurRealType = (onChange: (value: string) => void, value: string) => {
		const numValue = parseFloat(value);
		if (!isNaN(numValue)) {
			onChange(numValue.toFixed(3));
		} else if (value === "") {
			onChange("");
		}
	};

	useEffect(() => {
		if (show) {
			reset();
			if (sectionStand) {
				setValue("SectionId", sectionStand?.SectionId);
				setValue("StandId", sectionStand?.StandId);
				setValue("StandName", sectionStand?.StandName);
				setValue("SetpointGap", sectionStand?.SetpointGap);
				setValue("Comments", sectionStand?.Comments);
				setValue("StandTypeCode", sectionStand?.StandTypeCode ?? null);
				setValue("WidthMeasurement", sectionStand?.WidthMeasurement ?? null);
				setValue(
					"EdgerOrientationCode",
					sectionStand?.EdgerOrientationCode ?? null,
				);
				setValue("Area", sectionStand?.Area ?? null);
			}
		}
	}, [show, sectionStand]);

	return (
		<>
			{show && (
				<HelmetDexteel
					title={`Edit Stand ${sectionStand?.StandName} - Sections`}
				/>
			)}
			<MesfModal
				open={show}
				handleClose={() => onHide()}
				title={`Edit: Stand ${sectionStand?.StandName} | Gap ${
					sectionStand?.SetpointGap ? sectionStand?.SetpointGap : "-"
				}`}
				maxWidth="sm"
			>
				<form
					onSubmit={(e) => {
						e.preventDefault();
						e.stopPropagation();
						handleSubmit(onSubmit)(e);
					}}
				>
					<MesfModal.Content dividers>
						<Grid2 container justifyContent="center" spacing={2}>
							<Grid2 size={{ xs: 12, md: 2 }}>
								<Controller
									name="StandName"
									control={control}
									render={({ field }) => (
										<TextField
											fullWidth
											label="Stand"
											variant="outlined"
											size="small"
											value={field.value ?? ""}
											onChange={field.onChange}
											disabled
										/>
									)}
								/>
							</Grid2>
							<Grid2 size={{ xs: 12, md: 5 }}>
								<Controller
									name="SetpointGap"
									control={control}
									render={({ field }) => (
										<TextField
											label="Setpoint Gap"
											variant="outlined"
											fullWidth
											size="small"
											value={field.value ?? ""}
											onChange={(e) => onChangeRealType(e, field.onChange)}
											onBlur={() =>
												onBlurRealType(field.onChange, String(field.value))
											}
										/>
									)}
								/>
							</Grid2>
							<Grid2 size={{ xs: 12, md: 5 }}>
								<Controller
									name="Area"
									control={control}
									render={({ field }) => (
										<TextField
											label="Area"
											variant="outlined"
											fullWidth
											size="small"
											value={field.value ?? ""}
											onChange={(e) => onChangeRealType(e, field.onChange)}
											onBlur={() =>
												onBlurRealType(
													field.onChange,
													String(field.value ?? ""),
												)
											}
										/>
									)}
								/>
							</Grid2>
							{!isM1 && (
								<>
									<Grid2 size={{ xs: 12, md: standTypeCode ? 6 : 12 }}>
										<Controller
											name="StandTypeCode"
											control={control}
											rules={{ required: "Type is required" }}
											render={({ field, fieldState }) => (
												<TextField
													select
													fullWidth
													label="Type"
													variant="outlined"
													size="small"
													value={field.value ?? ""}
													onChange={field.onChange}
													error={!!fieldState.error}
													helperText={fieldState.error?.message}
												>
													{STAND_TYPE_OPTIONS.map((option) => (
														<MenuItem key={option.code} value={option.code}>
															{option.name}
														</MenuItem>
													))}
												</TextField>
											)}
										/>
									</Grid2>
									{standTypeCode === "U" && (
										<Grid2 size={{ xs: 12, md: 6 }}>
											<Controller
												name="WidthMeasurement"
												control={control}
												render={({ field }) => (
													<TextField
														label="Width Measurement"
														variant="outlined"
														fullWidth
														size="small"
														value={field.value ?? ""}
														onChange={(e) =>
															onChangeRealType(e, field.onChange)
														}
														onBlur={() =>
															onBlurRealType(
																field.onChange,
																String(field.value ?? ""),
															)
														}
													/>
												)}
											/>
										</Grid2>
									)}
									{standTypeCode === "E" && (
										<Grid2 size={{ xs: 12, md: 6 }}>
											<Controller
												name="EdgerOrientationCode"
												control={control}
												rules={{
													required: "Orientation is required for Edger",
												}}
												render={({ field, fieldState }) => (
													<TextField
														select
														fullWidth
														label="Edger Orientation"
														variant="outlined"
														size="small"
														value={field.value ?? ""}
														onChange={field.onChange}
														error={!!fieldState.error}
														helperText={fieldState.error?.message}
													>
														{EDGER_ORIENTATION_OPTIONS.map((option) => (
															<MenuItem key={option.code} value={option.code}>
																{option.name}
															</MenuItem>
														))}
													</TextField>
												)}
											/>
										</Grid2>
									)}
								</>
							)}
							<Grid2 size={{ md: 12, xs: 12 }}>
								<Controller
									name="Comments"
									control={control}
									render={({ field }) => (
										<TextField
											fullWidth
											label="Comments"
											variant="outlined"
											size="small"
											value={field.value ?? ""}
											onChange={field.onChange}
										/>
									)}
								/>
							</Grid2>
						</Grid2>
					</MesfModal.Content>
					<MesfModal.Actions style={{ padding: "15px 30px" }}>
						<FooterModalButtons
							isSubmitLoading={isSubmitLoading}
							onHide={() => onHide()}
						/>
					</MesfModal.Actions>
				</form>
			</MesfModal>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
